/**
 * What the customer actually owes.
 *
 * The cart can work out a subtotal, but not tax — the rules belong to the
 * branch, and there are currently two authorities charging different rates.
 * At the rate configured today the gap is 16%: a cashier giving change against
 * the subtotal on a Rs. 999 order hands back Rs. 160 too much, on every order,
 * with nothing on screen ever looking wrong.
 *
 * So the server is asked. It uses the same pricing code that will charge the
 * order, so the figure shown and the figure taken cannot disagree.
 *
 * WHY IT IS NOT ASKED ON EVERY TAP
 * --------------------------------
 * A cashier adds items in bursts. Quoting on each one puts a network round
 * trip between taps and makes the till feel slow, so the request is delayed
 * briefly and the last one wins.
 */
import { useEffect, useState } from 'react';

import { api, ApiError } from '../../lib/api.js';

/** Long enough to swallow a burst of taps, short enough not to be felt. */
const SETTLE_MS = 250;

export function useQuote(order, { enabled = true } = {}) {
  /**
   * The answer, tagged with the cart it belongs to.
   *
   * Keeping them together is what makes "is this total still current?" a
   * comparison rather than another piece of state to keep in step. A separate
   * `pending` flag drifts: it is set in one place and cleared in three, and
   * the one path that forgets leaves the Charge button enabled next to a total
   * that belongs to a previous cart.
   */
  const [answer, setAnswer] = useState({ key: null, quote: null, error: null });

  const items = order?.items ?? [];
  const active = enabled && items.length > 0;

  /**
   * Only the contents matter, not the object identity — the cart rebuilds the
   * order object on every render.
   *
   * The offer and the discount belong in here too. Left out, applying an
   * offer would not re-quote, and the cashier would read the old total beside
   * a bill that now has an offer on it — the exact staleness this key exists
   * to prevent.
   */
  const key =
    JSON.stringify(items) +
    (order?.orderType ?? '') +
    (order?.promotionCode ?? '') +
    // The zone and a hand-set delivery fee both move the total, so a change to
    // either has to re-quote — or the cashier reads a stale figure.
    (order?.deliveryZoneId ?? '') +
    (order?.deliveryFee ?? '') +
    JSON.stringify(order?.discounts ?? []);

  useEffect(() => {
    if (!active) return;

    const controller = new AbortController();

    const timer = setTimeout(async () => {
      try {
        const quote = await api.post('/pos/orders/quote', order, {
          signal: controller.signal,
        });
        setAnswer({ key, quote, error: null });
      } catch (caught) {
        if (caught.name === 'AbortError') return;

        // A stale quote beside a changed cart is worse than no quote: the
        // cashier reads a total that no longer belongs to what is on screen.
        setAnswer({
          key,
          quote: null,
          error:
            caught instanceof ApiError && caught.status === 400
              ? caught
              : new ApiError('Could not work out the total.', { offline: caught.offline }),
        });
      }
    }, SETTLE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, active]);

  // An empty cart has no total, rather than the last one it had — otherwise
  // the previous customer's figure sits on screen until the next tap.
  if (!active) return { quote: null, error: null, pending: false };

  const current = answer.key === key;

  return {
    quote: current ? answer.quote : null,
    error: current ? answer.error : null,
    // True from the instant the cart changes until its own answer arrives.
    pending: !current,
  };
}
